/* eslint-disable camelcase */
/* eslint-disable no-restricted-syntax */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';

import type { Products_Images } from 'src/gql/graphql';
import Imgix from 'shared/components/imgix';
import { ImgixDefaultSettings } from 'shared/constants';

type ActiveImage = {
  url: string;
  description: string;
};

type GalleryImageProps = {
  src: string;
  fallbackSrc: string;
  alt?: string;
  height: number;
  width: number;
  imgixAttrs?: Record<string, unknown>;
};

/**
 * GalleryImage displays an image with automatic fallback on error.
 * Handles its own error state internally.
 */
function GalleryImage({ src, fallbackSrc, alt = '', height, width, imgixAttrs = {} }: GalleryImageProps): JSX.Element {
  const [hasError, setHasError] = useState(false);

  // Reset error state when src changes to allow new images to load
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleError = useCallback((): void => {
    setHasError(true);
  }, []);

  return (
    <Imgix
      imgixParams={{ ...ImgixDefaultSettings, fit: 'max', ...imgixAttrs }}
      sizes='auto'
      width={width}
      height={height}
      htmlAttributes={{
        alt,
        loading: 'lazy',
        onError: handleError,
      }}
      src={hasError ? fallbackSrc : src}
    />
  );
}

type HoverImageGalleryProps = {
  images: Products_Images[];
  fallbackImage: string;
  alt?: string;
  height: number;
  width: number;
  imgixAttrs?: Record<string, unknown>;
  disableCycling?: boolean;
};

const MAX_GALLERY_IMAGES = 5;

/**
 * Filters images to only include active ones with valid URLs,
 * deduplicates by URL, and caps the result at MAX_GALLERY_IMAGES.
 */
const getActiveImages = (images: Products_Images[]): ActiveImage[] => {
  const seen = new Set<string>();
  const result: ActiveImage[] = [];

  for (const img of images) {
    if (!img.active || !img.url || seen.has(img.url)) {
      // eslint-disable-next-line no-continue
      continue;
    }
    seen.add(img.url);
    result.push({ url: img.url, description: img.description ?? '' });
    if (result.length >= MAX_GALLERY_IMAGES) {
      break;
    }
  }

  return result;
};

/**
 * HoverImageGallery displays product images that change based on horizontal mouse position.
 * When hovering, the image area is divided into equal segments for each image,
 * and moving the mouse left/right will cycle through the images.
 */
export default function HoverImageGallery({
  images,
  fallbackImage,
  alt = '',
  height,
  width,
  imgixAttrs = {},
  disableCycling = false,
}: HoverImageGalleryProps): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeImages = useMemo(() => getActiveImages(images), [images]);
  const hasMultipleImages = !disableCycling && activeImages.length > 1;

  // Reset activeIndex when images change or cycling is disabled
  // Handles list virtualization (different products sharing the same instance) and
  // ensures stale index doesn't persist if cycling is re-enabled after being disabled
  useEffect(() => {
    setActiveIndex(0);
  }, [images, disableCycling]);

  // Calculate image index based on horizontal position within container
  const updateIndexFromPosition = useCallback(
    (clientX: number): void => {
      if (!hasMultipleImages || !containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const relativeX = clientX - rect.left;
      const percentage = relativeX / rect.width;
      const newIndex = Math.min(Math.floor(percentage * activeImages.length), activeImages.length - 1);

      if (newIndex !== activeIndex && newIndex >= 0) {
        setActiveIndex(newIndex);
      }
    },
    [hasMultipleImages, activeImages.length, activeIndex]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      updateIndexFromPosition(event.clientX);
    },
    [updateIndexFromPosition]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>): void => {
      updateIndexFromPosition(event.touches[0].clientX);
    },
    [updateIndexFromPosition]
  );

  const currentIndex = disableCycling ? 0 : activeIndex;
  const currentImage = activeImages[currentIndex] ?? { url: fallbackImage, description: alt };

  return (
    <Container
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      $hasMultipleImages={hasMultipleImages}
    >
      <GalleryImage
        src={currentImage.url}
        fallbackSrc={fallbackImage}
        alt={alt}
        height={height}
        width={width}
        imgixAttrs={imgixAttrs}
      />

      {hasMultipleImages && (
        <IndicatorDots>
          {activeImages.map((image) => (
            <Dot key={image.url} data-testid='hover-gallery-dot' $isActive={image.url === currentImage.url} />
          ))}
        </IndicatorDots>
      )}
    </Container>
  );
}

const Container = styled.div<{ $hasMultipleImages?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;

  ${({ $hasMultipleImages }) =>
    $hasMultipleImages &&
    `
    cursor: pointer;
    touch-action: pan-y;
  `}
`;

const IndicatorDots = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  backdrop-filter: blur(4px);
`;

const Dot = styled.div<{ $isActive: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $isActive }) => ($isActive ? '#121516' : '#c8cdd0')};
  transition: background-color 150ms ease;
`;
